import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from "@heroicons/react/16/solid";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import useRouterHandlerHook from "../_hooks/useRouterHandlerHook";
import ButtonBase from "../Buttons/ButtonBase";
import TextInputBase from "../Inputs/TextInputBase";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

interface IBaseOption {
  id: string | number;
}
interface IMultiSearchSelectorProps<T extends IBaseOption> {
  label: string;
  placeholder: string;
  selectedValues: T[];
  options: T[];
  onChange: (value: T[] | null) => void;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  className?: string;
  optionsClassName?: string;
  disabled?: boolean;
  isRequired?: boolean;
  accessorKey?: string;
  showCount?: number;
  emptyText?: string;
  textEllipsis?: number;
  anchorPosition?: "top" | "bottom" | "left" | "right";
  settingActionText?: string;
  settingUrl?: string;
  error?: any;
  validateQuery?: (event: ChangeEvent<HTMLInputElement>) => void;
}

function MultiSearchSelector<T extends IBaseOption>({
  label,
  className,
  options = [],
  optionsClassName,
  disabled = false,
  selectedValues = [],
  placeholder,
  isRequired = true,
  accessorKey = "name",
  textEllipsis,
  query,
  setQuery,
  onChange,
  showCount = 3,
  emptyText,
  settingActionText,
  settingUrl,
  error,
  validateQuery,
  anchorPosition = "bottom",
}: IMultiSearchSelectorProps<T>) {
  const { navigate } = useRouterHandlerHook();
  const totalSelected = Number(selectedValues?.length) || 0;
  const [isOpen, setIsOpen] = useState(false);
  const prevOpen = useRef(false);
  function handleSearchQuery(event: ChangeEvent<HTMLInputElement>) {
    validateQuery?.(event);
    setQuery(event.target.value);
  }
  useEffect(() => {
    if (prevOpen.current === true && isOpen === false) {
      setQuery("");
    }
    prevOpen.current = isOpen;
  }, [isOpen]);

  const selectedIds = useMemo(
    () => new Set(selectedValues?.map((val) => val.id)),
    [selectedValues]
  );
  const filteredOptions = useMemo(
    () => options?.filter((opt) => !selectedIds.has(opt.id)),
    [options, selectedIds]
  );
  const updatedOptions = useMemo(
    () => [...(selectedValues ?? []), ...filteredOptions],
    [selectedValues, filteredOptions]
  );
  return (
    <div className=" w-full">
      {label && (
        <label className="text-xs flex items-center gap-1 text-left text-body font-semibold pl-1">
          {label}
          {isRequired && (
            <StarIcon className="size-2 text-error -translate-y-1" />
          )}
        </label>
      )}
      <Listbox
        value={Array.isArray(selectedValues) ? selectedValues : []}
        onChange={onChange}
        multiple
      >
        {({ open }) => {
          if (open !== isOpen) setIsOpen(open);
          return (
            <>
              <ListboxButton
                className={`relative min-w-full px-2 h-10 rounded-lg border-2 border-extraLightGray outline-none transition-colors   
                  focus:ring-primary focus:border-primary/50 disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-extraLightGray/30 
                  ${className}`}
                disabled={disabled}
              >
                <div className="flex items-center gap-2">
                  {totalSelected && showCount ? (
                    <>
                      {selectedValues?.slice(0, showCount).map((value: any) => (
                        <ButtonBase
                          disabled={disabled}
                          key={value?.id}
                          className="!user-select-none text-xs bg-primary/5 text-primary p-1"
                          buttonType="tertiary"
                          text={stringUtility.addEllipsisAtEnd(
                            String(value?.[accessorKey]),
                            textEllipsis
                          )}
                        />
                      ))}
                      {totalSelected > showCount && (
                        <div className="h-6 w-6 rounded-full bg-offWhite text-sm font-medium flex items-center justify-center ">
                          +{totalSelected - showCount}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-darkGray/80  font-medium">
                      {placeholder}
                    </span>
                  )}
                  <ChevronUpDownIcon
                    className="size-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray"
                    aria-hidden="true"
                  />
                </div>
              </ListboxButton>
              {error?.message && (
                <p className="text-xs text-error font-medium pl-2 mt-0.5">
                  {error?.message}
                </p>
              )}

              {open ? (
                <ListboxOptions
                  as="div"
                  anchor={anchorPosition}
                  portal
                  className="w-[var(--button-width)] [--anchor-gap:4px]  relative shadow-md border bg-white border-extraLightGray flex flex-col gap-2 rounded select-none outline-none z-50 px-2 pt-4 pb-1"
                >
                  <TextInputBase
                    value={query}
                    // onChange={(e) => setQuery(e?.target?.value)}
                    onChange={handleSearchQuery}
                    placeholder={`Search ${
                      label ? label?.toLocaleLowerCase() : ""
                    }...  `}
                    className="w-full h-8 bg-offWhite/50  "
                    icon={<MagnifyingGlassIcon className="size-4" />}
                  />
                  <div
                    className={`py-2 flex flex-col gap-1 max-h-80 overflow-y-auto ${optionsClassName}`}
                  >
                    {updatedOptions?.length ? (
                      updatedOptions?.map((optionValue: any) => {
                        return (
                          <ListboxOption
                            value={optionValue}
                            key={optionValue?.id}
                            className="flex cursor-pointer hover:bg-primary/20 rounded items-center w-full text-sm font-medium p-2"
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  data-selected={selected}
                                  className="rounded h-4 w-4 p-0.5 flex items-center justify-center ring-1 ring-extraLightGray data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:ring-primary bg-white"
                                >
                                  {selected && (
                                    <CheckIcon className="size-4 stroke-[3] text-inherit" />
                                  )}
                                </span>
                                <span className={`truncate ml-2 `}>
                                  {String(optionValue?.[accessorKey])}
                                </span>
                              </>
                            )}
                          </ListboxOption>
                        );
                      })
                    ) : (
                      <p className="text-sm px-2 font-medium text-darkGray text-center">
                        {emptyText || "No options found"}
                      </p>
                    )}
                  </div>
                  {settingActionText ? (
                    <div className="p-2 border-t border-extraLightGray">
                      <ButtonBase
                        onClick={() => {
                          navigate(String(settingUrl));
                        }}
                        buttonType="tertiary"
                        className="justify-start gap-2 flex-row-reverse text-body hover:text-primary/90"
                        icon={<Cog6ToothIcon className="size-4" />}
                        text={settingActionText}
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </ListboxOptions>
              ) : null}
            </>
          );
        }}
      </Listbox>
    </div>
  );
}

export default MultiSearchSelector;
